/**
 * @file 本だなカードの「この本のもの」メニュー。
 *
 * @what: アイコン (⋯) だけのボタンを押すと、その本にだけあるもの (さくいん・この本のために
 *   作った読みもの) を並べたメニューが上に開く。開くと最初の項目へ焦点が移り、↑↓ で移動、
 *   Esc と外側のクリックで閉じる (Esc はボタンへ焦点を戻す)。見た目と操作はヘッダーの
 *   メニュー (AccountMenu) に揃える。
 * @why: カードの操作を 1 行に収めるため、本によって有無が変わるものを 1 つのボタンへ畳む
 *   (代表 2026-09-03)。畳まないと、本ごとにボタンの数が変わって「開く」「きろくする」の
 *   位置が動く。
 * @failure: 描画のみ。項目が 0 件のときは呼び出し側がこの部品を出さない。
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./study.css";

export interface ShelfCardMenuItem {
  /** 一覧の key と、テストから引くための id。 */
  id: string;
  icon: string;
  label: string;
  to: string;
}

export function ShelfCardMenu({
  items,
  sourceTitle,
}: {
  items: readonly ShelfCardMenuItem[];
  /** 読み上げでどの本のメニューか分かるようにする。 */
  sourceTitle: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const menuItems = () =>
    Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);

  useEffect(() => {
    if (!open) return;
    menuItems()[0]?.focus();
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const list = menuItems();
      if (list.length === 0) return;
      e.preventDefault();
      const at = list.findIndex((el) => el === document.activeElement);
      const next =
        e.key === "ArrowDown" ? (at + 1) % list.length : (at - 1 + list.length) % list.length;
      list[next]?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="study-shelf-menu" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        className="study-shelf-btn study-shelf-btn--icon-only"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${sourceTitle} のこの本のもの`}
        title="この本のもの"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="study-shelf-btn-icon" aria-hidden="true">
          ⋯
        </span>
      </button>
      {open ? (
        <div className="app-menu-list study-shelf-menu-list" role="menu" aria-label="この本のもの">
          {items.map((item) => (
            <Link
              key={item.id}
              role="menuitem"
              className="app-menu-item"
              to={item.to}
              onClick={() => setOpen(false)}
            >
              <span className="study-shelf-menu-icon" aria-hidden="true">
                {item.icon}
              </span>
              {item.label}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}
